type CrushOptions = {
  blackPoint?: number; // 0..255 (try 45)
  gamma?: number;      // try 1.05..1.25
  contrast?: number;   // 1 = none, try 1.1..1.25
};

export async function crushBlacksToDataURL(
  src: string,
  { blackPoint = 45, gamma = 1.05, contrast = 1.15 }: CrushOptions = {}
): Promise<string> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = src;

  await new Promise<void>((res, rej) => {
    img.onload = () => res();
    img.onerror = () => rej(new Error("Image load failed"));
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  ctx.drawImage(img, 0, 0);

  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // helpers
  const clamp = (v: number) => Math.max(0, Math.min(255, v));
  const applyContrast = (v: number) => (v - 128) * contrast + 128;

  for (let i = 0; i < data.length; i += 4) {
    // Since it's grayscale, we can treat luminance simply,
    // but this still works for color images.
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];

    // 1) Contrast around mid
    r = applyContrast(r);
    g = applyContrast(g);
    b = applyContrast(b);

    // 2) Crush black point (lift-down)
    r = ((r - blackPoint) * 255) / (255 - blackPoint);
    g = ((g - blackPoint) * 255) / (255 - blackPoint);
    b = ((b - blackPoint) * 255) / (255 - blackPoint);

    // 3) Gamma (nice rolloff)
    r = 255 * Math.pow(clamp(r) / 255, gamma);
    g = 255 * Math.pow(clamp(g) / 255, gamma);
    b = 255 * Math.pow(clamp(b) / 255, gamma);

    data[i] = clamp(r);
    data[i + 1] = clamp(g);
    data[i + 2] = clamp(b);
  }

  const imageData = new ImageData(new Uint8ClampedArray(data), width, height);
  ctx.putImageData(imageData, 0, 0);

  return canvas.toDataURL("image/jpeg", 0.92);
}
